import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDashboardUI } from '../../context';

const DEBOUNCE_MS = 500;

export const useDashboardFilters = () => {
  const { filters, setFilters } = useDashboardUI();
  const [localSearch, setLocalSearch] = useState(filters.search || '');
  const searchTimeoutRef = useRef(null);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setFilters({ search: localSearch });
    }, DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [localSearch, setFilters]);

  const handleStateChange = useCallback(
    (value) => {
      setFilters({ state: value || '' });
    },
    [setFilters]
  );

  const handleSectionChange = useCallback(
    (value) => {
      setFilters({ sectionId: value || '' });
    },
    [setFilters]
  );

  const resetFilters = useCallback(() => {
    setLocalSearch('');
    setFilters({ search: '', state: '', sectionId: '' });
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, [setFilters]);

  const hasActiveFilters = useMemo(
    () =>
      localSearch.trim() !== '' ||
      (filters.state || '').trim() !== '' ||
      (filters.sectionId || '').trim() !== '',
    [localSearch, filters.state, filters.sectionId]
  );

  return {
    localSearch,
    setLocalSearch,
    filters,
    handleStateChange,
    handleSectionChange,
    resetFilters,
    hasActiveFilters,
  };
};
